import { type Router, type RouteRecordRaw } from 'vue-router'
import { useUserAttributes } from '@/modules/miiaFw/composables/useUserAttributes'
import { routes } from '.'
import type { RouterPath } from '@/types/router/path'
import type { EmergencyAuthority } from '@/constants/router/auth'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'
import type { FeatureOptions } from '@/types/accessor/api/governmentSetting/governmentSetting'

export const useRouterUtils = (injectedRouter: Router) => {
  const { getUserAttributesFromStore } = useUserAttributes()
  const router = injectedRouter

  /**
   * 指定した自治体機能権限を現在の設定で保持しているか確認する
   * @param {FeatureOptions} featureOption - 確認したい機能権限のオプション名
   * @returns {boolean} 指定した機能権限が含まれている場合はtrue、それ以外はfalse
   */
  const hasFeatureOption = (featureOption: FeatureOptions) => {
    const { featureOptions } = useGovernmentSettingStore()
    return featureOptions.includes(featureOption)
  }

  /**
   * ルート配列と親パスを受け取り、各ルートのpathに親パスを連結した
   * フルパスを再帰的に生成してフラットな配列で返す
   * @param {RouteRecordRaw[]} routes - 対象のルート配列
   * @param {string} [parentPath=''] - 親パス（省略時は空文字）
   * @returns {RouteRecordRaw[]} フルパス付きの全ルートのフラット配列
   */
  const getAllRoutesWithFullPath = (
    routes: RouteRecordRaw[],
    parentPath: string = ''
  ): RouteRecordRaw[] => {
    const result: RouteRecordRaw[] = []

    routes.forEach(route => {
      const fullPath = route.path.startsWith('/') ? route.path : `${parentPath}/${route.path}`

      const newRoute: RouteRecordRaw = {
        ...route,
        path: fullPath,
        children: []
      }
      result.push(newRoute)
      if (route.children) {
        result.push(...getAllRoutesWithFullPath(route.children, fullPath))
      }
    })
    return result
  }

  /**
   * 指定の権限リストに基づいてアクセス可能なルートを絞り込み取得する。
   * ルートのmeta.requiredAuthoritiesと照合し、権限がすべて含まれているものを対象とする。
   * routerPathが指定された場合は、該当パスの権限チェックのみ行う。
   * @param {EmergencyAuthority[]} authorities - ユーザーの権限リスト
   * @param {RouterPath} [routerPath] - 取得対象の特定パス（未指定の場合は全てのアクセス可能なルートを返す）
   * @returns {RouteRecordRaw[]} 指定された権限でアクセス可能なルートのリスト
   */
  const getAuthorizedPaths = (
    authorities: EmergencyAuthority[],
    routerPath?: RouterPath
  ): RouteRecordRaw[] => {
    const allRoutes = getAllRoutesWithFullPath(routes)
    // router定義より権限設定されているページを抽出
    const guardRequiredRoutes = allRoutes.filter(
      route => route.meta?.requiredAuthorities !== undefined
    )

    //各ページのrequiredAuthoritiesについて、
    const assessableRoutes = guardRequiredRoutes.filter(guardRequiredRoute => {
      const requiredAuthorities = guardRequiredRoute.meta
        ?.requiredAuthorities as EmergencyAuthority[]
      const isAuthorized = requiredAuthorities.every(requiredAuth => {
        return authorities.some(
          auth =>
            requiredAuth.scope == auth.scope &&
            requiredAuth.actions.every(action => auth.actions.includes(action))
        )
      })
      //routerPathを受け取っている場合、そのrouterPathの権限があるかをチェック
      if (routerPath) {
        return isAuthorized && guardRequiredRoute.path === routerPath
      } else {
        return isAuthorized
      }
    })
    return assessableRoutes
  }

  /**
   * 指定されたパス（または全パス）へのアクセス権限をチェックし、
   * アクセス可能なルート配列を返す。
   * @param {RouterPath} [routerPath] - アクセス可能なルートを絞り込むための特定パス（省略可）
   * @returns {Promise<RouteRecordRaw[]>} アクセス可能なルートのPromise解決結果
   */
  const getAuthorizedEmergencyPaths = async (
    routerPath?: RouterPath
  ): Promise<RouteRecordRaw[]> => {
    try {
      const attribute = await getUserAttributesFromStore()
      const authority = attribute.attribute['disaster-emergency'].authority
      //ユーザ属性のattributeを取得し、各ページの権限と照合
      return getAuthorizedPaths(authority, routerPath)
    } catch {
      // ユーザーの権限情報の取得に失敗（ログインされていないまま、値打ちの画面遷移の可能性）
      // アクセスの可能な画面なし
      return []
    }
  }

  /**
   * 指定されたパスに対して権限チェックを行い、
   * そのパスへのアクセス権限があれば画面遷移を行う非同期関数。
   * アクセス可能なルートが見つからなければ遷移しない。
   * @param {RouterPath} routerPath - 画面遷移を試みる対象のルートパス
   * @returns {Promise<void>} 遷移処理の完了を待機するPromise
   */
  const transition = async (routerPath: RouterPath) => {
    const authorizedPaths = await getAuthorizedEmergencyPaths(routerPath)
    if (authorizedPaths.length != 0) {
      router.push({ path: authorizedPaths[0].path })
    }
  }

  return {
    routes,
    getAuthorizedPaths,
    getAuthorizedEmergencyPaths,
    hasFeatureOption,
    transition
  }
}
